'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, Loader2, Calendar, CheckCircle, Clock, MapPin, Phone } from 'lucide-react';
import {
  householdsAPI,
  HouseholdMember,
  FollowUp,
  FollowUpCreate,
  FollowUpStatus,
  ContactMethod,
  PathwayType,
  PATHWAY_LABELS,
} from '@/services/api/households';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate, useUserTimezone } from '@/hooks/useUserTimezone';
import { isValidTimeZone, zonedInputsToMs } from '@/utils/zonedTime';
import { TimeZoneSelect } from '@/components/ui/TimeZoneSelect';
import { ItemZoneNote } from '@/components/ui/ItemZoneNote';

const STATUS_COLORS: Record<FollowUpStatus, string> = {
  SCHEDULED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  MISSED: 'bg-red-100 text-red-800',
  RESCHEDULED: 'bg-orange-100 text-orange-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
};

const CONTACT_METHOD_LABELS: Record<ContactMethod, string> = {
  PHONE: 'Phone',
  IN_PERSON: 'In Person',
  VIDEO: 'Video',
  EMAIL: 'Email',
  HOME_VISIT: 'Home Visit',
  OTHER: 'Other',
};

interface Props {
  householdId: number;
  members: HouseholdMember[];
}

export default function HouseholdFollowUpsTab({ householdId, members }: Props) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // TRUS-697: a follow-up's date + time are in its own zone (default the user's).
  const userTimezone = useUserTimezone();
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FollowUpCreate>({
    next_contact_date: '',
    pathways_identified: [],
  });

  const loadFollowUps = useCallback(async () => {
    setLoading(true);
    try {
      const data = await householdsAPI.listFollowUps(householdId);
      setFollowUps(data);
    } catch (err) {
      console.error('Failed to load follow-ups:', err);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    loadFollowUps();
  }, [loadFollowUps]);

  const handleCreate = async () => {
    if (!form.next_contact_date) return;
    setSaving(true);
    try {
      await householdsAPI.createFollowUp(householdId, { ...form, timezone: form.timezone || userTimezone });
      setShowAdd(false);
      setForm({ next_contact_date: '', pathways_identified: [] });
      loadFollowUps();
    } catch (err) {
      console.error('Failed to create follow-up:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (fuId: number) => {
    const notes = prompt('Completion notes (optional):');
    try {
      await householdsAPI.updateFollowUp(householdId, fuId, {
        status: 'COMPLETED',
        completed_notes: notes || undefined,
      });
      loadFollowUps();
    } catch (err) {
      console.error('Failed to complete follow-up:', err);
    }
  };

  const handleStatusChange = async (fuId: number, newStatus: FollowUpStatus) => {
    try {
      await householdsAPI.updateFollowUp(householdId, fuId, { status: newStatus });
      loadFollowUps();
    } catch (err) {
      console.error('Failed to update follow-up:', err);
    }
  };

  const togglePathway = (pathway: PathwayType) => {
    setForm((prev) => {
      const current = prev.pathways_identified || [];
      const next = current.includes(pathway)
        ? current.filter((p) => p !== pathway)
        : [...current, pathway];
      return { ...prev, pathways_identified: next };
    });
  };

  const activeMembers = members.filter((m) => m.status === 'ACTIVE');
  const isOverdue = (fu: FollowUp) =>
    fu.status === 'SCHEDULED' && new Date(fu.next_contact_date) < new Date();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Follow-ups & Next Steps</h3>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="gap-1.5">
          <Plus className="w-4 h-4" />
          Schedule Follow-up
        </Button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Schedule Follow-up Contact</h4>
              <button onClick={() => setShowAdd(false)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contact Date *</Label>
                <Input
                  type="date"
                  value={form.next_contact_date}
                  onChange={(e) => setForm((prev) => ({ ...prev, next_contact_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Contact Time</Label>
                <Input
                  type="time"
                  value={form.next_contact_time || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, next_contact_time: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="follow-up-timezone">{t('time.zone.label', 'Time zone')}</Label>
              <TimeZoneSelect
                id="follow-up-timezone"
                value={form.timezone || userTimezone}
                onChange={(tz) => setForm((prev) => ({ ...prev, timezone: tz }))}
                userTimeZone={userTimezone}
                data-testid="follow-up-timezone"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contact Method</Label>
                <select
                  value={form.contact_method || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      contact_method: (e.target.value || undefined) as ContactMethod | undefined,
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select...</option>
                  {Object.entries(CONTACT_METHOD_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={form.contact_location || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, contact_location: e.target.value }))}
                  placeholder="e.g. Office, Home visit address"
                />
              </div>
            </div>

            <div>
              <Label>For (optional)</Label>
              <select
                value={form.household_member_id || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    household_member_id: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="">Entire Household</option>
                {activeMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.first_name} {m.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Pathway Multi-select */}
            <div>
              <Label>Pathway Identified</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.entries(PATHWAY_LABELS).map(([val, label]) => {
                  const selected = (form.pathways_identified || []).includes(val as PathwayType);
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => togglePathway(val as PathwayType)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        selected
                          ? 'bg-indigo-100 text-indigo-800 border-indigo-300'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                value={form.notes || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]"
                placeholder="Follow-up purpose, agenda items..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={!form.next_contact_date || saving} onClick={handleCreate}>
                {saving ? 'Scheduling...' : 'Schedule Follow-up'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Follow-ups List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        </div>
      ) : followUps.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No follow-ups scheduled</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {followUps.map((fu) => {
            const overdue = isOverdue(fu);
            return (
              <Card
                key={fu.id}
                className={overdue ? 'border-red-200 bg-red-50/30' : ''}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-sm">
                          {(() => {
                            // TRUS-697: a timed follow-up is shown in the viewer's zone,
                            // with its own time when it was scheduled in another zone.
                            const at =
                              fu.next_contact_time && isValidTimeZone(fu.timezone)
                                ? zonedInputsToMs(String(fu.next_contact_date).slice(0, 10), String(fu.next_contact_time).slice(0, 5), fu.timezone)
                                : null;
                            if (at == null) {
                              return (
                                <>
                                  {new Date(fu.next_contact_date).toLocaleDateString()}
                                  {fu.next_contact_time && ` at ${fu.next_contact_time}`}
                                </>
                              );
                            }
                            return (
                              <>
                                {fmt.dateTime(new Date(at).toISOString())}{' '}
                                <ItemZoneNote value={at} itemTimeZone={fu.timezone} kind="followUp" className="font-normal text-gray-500" />
                              </>
                            );
                          })()}
                        </span>
                        <Badge className={STATUS_COLORS[fu.status]} variant="secondary">
                          {fu.status}
                        </Badge>
                        {overdue && (
                          <Badge className="bg-red-100 text-red-800" variant="secondary">
                            Overdue
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {fu.follow_up_for || 'Household'}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                        {fu.contact_method && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {CONTACT_METHOD_LABELS[fu.contact_method] || fu.contact_method}
                          </span>
                        )}
                        {fu.contact_location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {fu.contact_location}
                          </span>
                        )}
                      </div>

                      {/* Pathways */}
                      {fu.pathways_identified && fu.pathways_identified.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {fu.pathways_identified.map((p) => (
                            <Badge key={p} variant="outline" className="text-xs">
                              {PATHWAY_LABELS[p] || p}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {fu.notes && <p className="text-xs text-gray-500 mt-1">{fu.notes}</p>}
                      {fu.completed_notes && (
                        <p className="text-xs text-green-700 mt-1">Completed: {fu.completed_notes}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      {fu.status === 'SCHEDULED' && (
                        <Button size="sm" variant="outline" onClick={() => handleComplete(fu.id)}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          Complete
                        </Button>
                      )}
                      <select
                        value={fu.status}
                        onChange={(e) => handleStatusChange(fu.id, e.target.value as FollowUpStatus)}
                        className="border rounded px-2 py-1 text-xs"
                      >
                        <option value="SCHEDULED">Scheduled</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="MISSED">Missed</option>
                        <option value="RESCHEDULED">Rescheduled</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
